// Primitivos visuales de la ficha (`.fk-card`, `.fk-kv`, `.fk-chip` del shared del mock) y el
// envoltorio que decide entre cargando, la falla del bloque y el contenido.
import type { ReactNode } from 'react';
import { AlertTriangle, Loader2, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { describeFailure, isRetryableFailure, type CreditFileFailure } from '../failures';
import type { CreditAlert, CreditTone } from '../logic';

const TONE_CLASS: Record<CreditTone, string> = {
    success: 'bg-success-soft text-success',
    danger: 'bg-danger-soft text-danger',
    warning: 'bg-warning-soft text-warning',
    info: 'bg-info-soft text-info',
    neutral: 'bg-muted text-muted-foreground',
};

const ALERT_CLASS: Record<CreditTone, string> = {
    success: 'bg-success-soft text-success',
    danger: 'bg-danger-soft text-danger',
    warning: 'bg-warning-soft text-warning',
    info: 'bg-info-soft text-info',
    neutral: 'bg-muted text-muted-foreground',
};

export function CreditChip({
    tone = 'neutral',
    className,
    children,
}: {
    tone?: CreditTone;
    className?: string;
    children: ReactNode;
}) {
    return (
        <span
            className={cn(
                'inline-flex items-center rounded-full px-1.5 py-[1px] text-[9.5px] font-bold',
                TONE_CLASS[tone],
                className,
            )}
        >
            {children}
        </span>
    );
}

// Los primitivos de tarjeta viven en shared; Cartera los sigue llamando con su nombre de siempre.
export {
    DetailCard as CreditCard,
    DetailKeyValue as CreditKeyValue,
    GapMark as CreditGapMark,
} from '@/shared/patterns/DetailCard';

/**
 * Alerta contextual del crédito, con el tono de su estado (`.fk-alert-*` del mock). Habla del
 * crédito —mora, activación, liquidación—, no de qué datos faltan.
 */
export function CreditAlertBox({ alert }: { alert: CreditAlert }) {
    return (
        <p
            role="status"
            className={cn(
                'mt-2 rounded px-3 py-2 text-[11px] leading-relaxed',
                ALERT_CLASS[alert.tone],
            )}
        >
            {alert.text}
        </p>
    );
}

export function CreditEmptyNote({ children }: { children: ReactNode }) {
    return (
        <p className="px-5 py-[30px] text-center text-[12px] italic text-muted-foreground">
            {children}
        </p>
    );
}

export function CreditBlockLoading({ label }: { label: string }) {
    return (
        <div
            role="status"
            className="flex items-center justify-center gap-2 px-2 py-6 text-[11.5px] text-muted-foreground"
        >
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            {label}
        </div>
    );
}

/**
 * La falla de UN bloque. El de permiso NO ofrece reintentar —reintentar no da acceso— y el 404
 * tampoco; solo la falla de lectura trae el botón.
 */
export function CreditBlockError({
    failure,
    subject,
    onRetry,
}: {
    failure: CreditFileFailure;
    /** Nombra el bloque que falló: «la amortización», «los documentos». */
    subject: string;
    onRetry: () => void;
}) {
    const described = describeFailure(failure, subject);
    return (
        <div role="alert" className="px-2 py-5 text-center">
            <AlertTriangle className="mx-auto mb-1.5 size-5 text-muted-foreground/50" aria-hidden />
            <p className="text-[12px] font-semibold">{described.title}</p>
            <p className="mx-auto mt-1 max-w-[42ch] text-[10.5px] leading-relaxed text-muted-foreground">
                {described.body}
            </p>
            {isRetryableFailure(failure) ? (
                <button
                    type="button"
                    onClick={onRetry}
                    className="mt-3 inline-flex items-center gap-1.5 rounded border border-border px-2.5 py-1 text-[11px] font-medium transition-colors hover:bg-accent"
                >
                    <RotateCcw className="size-3" aria-hidden />
                    Reintentar
                </button>
            ) : null}
        </div>
    );
}

/** Cargando · falla · contenido, en ese orden. Es lo que envuelve a cada bloque de cada pestaña. */
export function CreditBlock({
    isLoading,
    failure,
    subject,
    onRetry,
    children,
}: {
    isLoading: boolean;
    failure: CreditFileFailure | null;
    subject: string;
    onRetry: () => void;
    children: ReactNode;
}) {
    if (failure) {
        return <CreditBlockError failure={failure} subject={subject} onRetry={onRetry} />;
    }
    if (isLoading) return <CreditBlockLoading label={`Cargando ${subject}…`} />;
    return <>{children}</>;
}
